import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { JourneeView } from './formateur/journee/JourneeView';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JourneeDetailPage = () => {
  const { dossierId, date } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.state?.from === 'fil_chronologique') {
      navigate(`/dossiers/${dossierId}/edit?tab=fil_chronologique&from=apercu`);
    } else {
      navigate('/planning');
    }
  };

  const [journeeData, setJourneeData] = useState(null);
  const [cahier, setCahier] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasEvaluation, setHasEvaluation] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [resp, modulesRes] = await Promise.all([
        axios.get(`${API_URL}/portail/formateur/journee/${dossierId}/${date}`),
        axios.get(`${API_URL}/modules`),
      ]);

      const data = resp.data;
      setJourneeData(data);
      setModules(modulesRes.data);
      setHasEvaluation(!!data.has_evaluations);

      const existing = data.cahier_de_texte;
      const moduleInfo = data.module_info;
      const candidats = data.candidats || [];

      // Initialize evaluations
      let evals = [];
      if (existing?.evaluations_realisees?.length > 0) {
        evals = existing.evaluations_realisees;
      } else if (moduleInfo?.evaluations) {
        evals = moduleInfo.evaluations.map(ev => ({
          evaluation_type: ev.type,
          evaluation_sous_type: ev.sous_type || '',
          statut: 'a_faire',
          lien_google_form: ev.details || '',
          commentaire: '',
        }));
      }

      // Initialize modules_realises
      let modulesRealises = [];
      if (existing?.modules_realises?.length > 0) {
        modulesRealises = existing.modules_realises;
      } else {
        const plannedModules = data.planning_day?.modules_pedagogiques || [];
        if (plannedModules.length > 0) {
          modulesRealises = plannedModules
            .filter(mp => mp.module_id)
            .map(mp => {
              const mod = modulesRes.data.find(m => m.id === mp.module_id);
              return {
                module_id: mp.module_id,
                titre: mod?.titre || mp.module_id,
                duree_heures: mp.duree_heures || 7,
              };
            });
        } else if (moduleInfo) {
          modulesRealises = [{ module_id: moduleInfo.id, titre: moduleInfo.titre, duree_heures: 7 }];
        }
      }

      // Merge presences
      const existingPresMap = {};
      (existing?.presences || []).forEach(p => { existingPresMap[p.candidat_id] = p; });
      const enAftInit = resp.data.candidats_en_aft || {};
      const mergedPresences = candidats.map(c => existingPresMap[c.id] || (enAftInit[c.id] ? { candidat_id: c.id, statut: 'en_aft', aft_code: enAftInit[c.id].aft_code, heure_arrivee: '', commentaire: '' } : { candidat_id: c.id, statut: 'present', heure_arrivee: '', commentaire: '' }));

      // Merge observations
      const existingObsMap = {};
      (existing?.observations_candidats || []).forEach(o => { existingObsMap[o.candidat_id] = o; });
      const mergedObservations = candidats.map(c => existingObsMap[c.id] || { candidat_id: c.id, texte: '' });

      setCahier({
        module_realise_id: existing?.module_realise_id || moduleInfo?.id || '',
        module_realise_titre: existing?.module_realise_titre || moduleInfo?.titre || '',
        modules_realises: modulesRealises,
        modules_confirmes: existing?.modules_confirmes || false,
        presences: mergedPresences,
        presences_validees: existing?.presences_validees || false,
        abandons: existing?.abandons || [],
        deroule: existing?.deroule || '',
        exercices: existing?.exercices || '',
        cas_pratique: existing?.cas_pratique || '',
        observations_groupe: existing?.observations_groupe || '',
        actions_preconisations: existing?.actions_preconisations || '',
        note_interne: existing?.note_interne || '',
        evaluations_realisees: evals,
        observations_candidats: mergedObservations,
        modules_candidats: existing?.modules_candidats || [],
        dispatch_modules: existing?.dispatch_modules || [],
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement de la journée');
    } finally {
      setLoading(false);
    }
  }, [dossierId, date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveJournee = async (override) => {
    if (!cahier) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/portail/formateur/journee/${dossierId}/${date}`, override || cahier);
      toast.success('Cahier de texte sauvegardé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!journeeData || !cahier) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Journée non trouvée</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/planning')}>Retour au planning</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <JourneeView
        journeeData={journeeData}
        cahier={cahier}
        setCahier={setCahier}
        modules={modules}
        saving={saving}
        onSave={saveJournee}
        onBack={handleBack}
        hasEvaluation={hasEvaluation}
        setHasEvaluation={setHasEvaluation}
        onRefresh={fetchData}
        onOpenAction={(dossierId, tab = 'detail') => navigate(`/dossiers/${dossierId}/edit?tab=${{ timeline: 'fil_chronologique', bilan: 'bilan' }[tab] || 'general'}`)}
      />
    </DashboardLayout>
  );
};

export default JourneeDetailPage;
